import React from 'react'
import classNames from 'classnames'
import {NullAsDefault} from 'views/Shared'
import {renderIf} from "../../../Shared";
import copyToClipboard from "copy-to-clipboard";
import {message} from "antd";
import {SubjectActionTypes} from "../../../AppLocals/Course";
import {IoCopyOutline} from "react-icons/io5";
import {BsCalendarCheck, BsChevronRight, BsEyeFill} from "react-icons/bs";
import {ClassroomSvg} from "../../../../components/Svgs/ClassroomSvg";
import {getGroupColor} from "./renderCourseClassStudent";

const splitHashtags = (hashtags) => (hashtags || '').split(',').filter((e) => !!e)

// Avatar môn học = chữ cái đầu của 2 từ đầu tên (vd "Hôn Nhân" -> "HN"), tô màu theo tên qua
// getGroupColor dùng chung (giống avatar chữ bên Học viên - renderStudent.js).
const getSubjectInitials = (title) => {
    if (!title) return '?'
    const words = title.trim().split(/\s+/).filter(Boolean)
    return (words.slice(0, 2).map((w) => w.charAt(0)).join('') || '?').toUpperCase()
}

const SubjectAvatar = ({title, size = 32}) => (
    <div
        style={{width: size, height: size, backgroundColor: getGroupColor(title), flexShrink: 0}}
        className="rounded-full flex flex-center">
        <span className={classNames('text-white font-bold leading-none', size >= 40 ? 'text-sm' : 'text-xs')}>
            {getSubjectInitials(title)}
        </span>
    </div>
)

export const renderSubject = ({translate: t, renderContextMenu = NullAsDefault, handleAction = NullAsDefault} = {}) => (item, index) => {
    return (
        <div key={index} style={{ fontSize: 14 }} className="p-4 flex flex-col custom-item-render relative list-item">
            <h2
                onClick={() => {
                    handleAction(SubjectActionTypes.SUBJECT_READ, item)
                }}
                className="font-bold text-base text-gray-800 tracking-wide hover:text-primary cursor-pointer">
                {item?.title}
            </h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 leading-none">
                {renderIf(
                    item?.subject_idcode,
                    <span
                        onClick={() => {
                            copyToClipboard(item?.subject_idcode)
                            message.success(t('copied'))
                        }}
                        className="flex items-center gap-1 px-2 py-1 bg-secondary-50 border border-secondary-200 rounded-full text-sm font-mono font-semibold text-secondary hover:text-secondary-600 tracking-wider cursor-pointer leading-none">
                        {item?.subject_idcode}
                        <IoCopyOutline size={12}/>
                    </span>
                )}
                {renderIf(
                    item?.number_of_sessions > 0,
                    <span className="flex items-center gap-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded-full text-slate-500 tracking-wide leading-none">
                        <BsCalendarCheck size={12}/>
                        {`${item?.number_of_sessions} ${t('sessions')}`}
                    </span>
                )}
                {renderIf(
                    item?.course_class_count > 0,
                    <span className="flex items-center gap-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded-full text-slate-500 tracking-wide leading-none">
                        <ClassroomSvg size={12}/>
                        {t('classes used', {count: item?.course_class_count})}
                    </span>
                )}
            </div>
            {renderIf(
                item?.hashtags,
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {(item?.hashtags || '').split(',').filter((e) => !!e).map((tag) => (
                        <span
                            key={tag}
                            style={{padding: '1px 8px'}}
                            className="rounded-full font-medium text-2xs text-color-300 bg-color-50 tracking-wide">
                            {`#${tag}`}
                        </span>
                    ))}
                </div>
            )}
            {renderIf(
                item?.description,
                <div className="mt-3 pt-3 border-t border-color-50 text-color-300 tracking-wider max-lines-3 whitespace-pre-line 1111">
                    {item?.description}
                </div>
            )}
            <div className="absolute right-8 top-8 contextIcon">
                {renderContextMenu(item)}
            </div>
        </div>
    )
}

// Layout "Bảng" (mặc định) + "Thẻ" cho trang Kho Môn học (Subjects.js) - theo mockup dạng bảng
// giống trang Khóa học. KHÔNG đổi renderSubject gốc phía trên. Rút gọn field so với bản gốc
// (bỏ khối mô tả dài tách dòng, chỉ còn mô tả ngắn 2 dòng) - chi tiết đầy đủ vẫn xem qua
// SubjectActionTypes.SUBJECT_READ.
// Style mã định danh dùng chung (mã học viên / mã môn học / mã giáo viên / ...): "#CODE" chữ
// mono xanh dương, bấm để copy. `pill` = bọc nền bo tròn (dùng trong thẻ); mặc định phẳng
// (dùng trong bảng). Khớp cách hiển thị mã học viên ở renderStudent.js.
const SubjectIdCode = ({code, t, pill = false}) => renderIf(
    code,
    <span
        onClick={() => {
            copyToClipboard(code)
            message.info(t('copied'))
        }}
        className="w-fit flex items-center gap-1 font-mono text-blue-500 hover:text-blue-700 cursor-pointer whitespace-no-wrap text-xs">
        {`#${code}`}
        <IoCopyOutline size={9}/>
    </span>
)

const SubjectUsagePills = ({item, t}) => (
    <div className="flex flex-col gap-1 text-2xs">
        {renderIf(
            item?.number_of_sessions > 0,
            <span className="text-xs text-color-400 whitespace-no-wrap">
                {`${t('number sessions')}: `}
                <span className="font-medium text-gray-600">
                    {`${item?.number_of_sessions} ${t('sessions')}`}
                </span>
            </span>
        )}
        {renderIf(
            item?.course_class_count > 0,
            <span className="text-xs text-color-400 whitespace-no-wrap">
                {`${t('classes used')}: `}
                <span className="font-medium text-gray-600">
                    {`${item?.course_class_count} ${t('class').toLowerCase()}`}
                </span>
            </span>
        )}
    </div>
)

const SubjectHashtags = ({hashtags}) => {
    const tags = splitHashtags(hashtags)
    if (!tags.length) return null
    return (
        <div className="flex flex-wrap items-center gap-1">
            {tags.map((tag) => (
                <span
                    key={tag}
                    className="text-2xs font-semibold text-rose-500 whitespace-no-wrap">
                    {`#${tag}`}
                </span>
            ))}
        </div>
    )
}

export const renderSubjectTableRow = ({translate: t, renderContextMenu = NullAsDefault, handleAction = NullAsDefault} = {}) => (item, index) => {
    return (
        <tr key={index} className="border-t border-color-50 background hover:background-50 transition-colors align-top">
            {/* MÔN HỌC (gộp cả mô tả xuống dưới tên) */}
            <td className="px-3 py-2.5">
                <div className="flex items-start gap-2.5 min-w-0" style={{maxWidth: 420}}>
                    <SubjectAvatar title={item?.title}/>
                    <div className="flex flex-col gap-1 min-w-0">
                        <span
                            onClick={() => handleAction(SubjectActionTypes.SUBJECT_READ, item)}
                            className="font-bold text-sm text-color-000 hover:text-primary cursor-pointer leading-snug">
                            {item?.title}
                        </span>
                        <SubjectHashtags hashtags={item?.hashtags}/>
                        {renderIf(
                            item?.description,
                            <div className="text-xs text-color-300 tracking-wider max-lines-2 whitespace-pre-line">
                                {item?.description}
                            </div>
                        )}
                    </div>
                </div>
            </td>
            {/* MÃ MÔN HỌC */}
            <td className="px-3 py-2.5">
                {item?.subject_idcode
                    ? <SubjectIdCode code={item?.subject_idcode} t={t}/>
                    : <span className="text-xs text-color-300 italic">{t('not update')}</span>}
            </td>
            {/* SỐ TIẾT HỌC (chỉ value, không nhắc lại nhãn, không hiển thị lớp học) */}
            <td className="px-3 py-2.5">
                {item?.number_of_sessions > 0
                    ? (
                        <span className="text-sm font-semibold text-gray-700 whitespace-no-wrap">
                            {`${item?.number_of_sessions} ${t('sessions')}`}
                        </span>
                    )
                    : <span className="text-xs text-color-300 italic">{t('not update')}</span>}
            </td>
            {/* THAO TÁC */}
            <td className="px-3 py-2.5">
                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => handleAction(SubjectActionTypes.SUBJECT_READ, item)}
                        style={{width: 28, height: 28}}
                        className="flex flex-center rounded-full border border-color-200 hover:border-primary hover:background-100 text-color-400 hover:text-primary">
                        <BsEyeFill size={13}/>
                    </button>
                    {renderContextMenu(item)}
                </div>
            </td>
        </tr>
    )
}

export const renderSubjectCardItem = ({translate: t, renderContextMenu = NullAsDefault, handleAction = NullAsDefault} = {}) => (item, index) => {
    return (
        <div
            key={index}
            className="p-4 flex flex-col gap-2.5 border border-color-100 background rounded-2xl hover:shadow-md transition-all duration-300 relative custom-item-render">
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                    <SubjectAvatar title={item?.title} size={40}/>
                    <div className="flex flex-col">
                        <span
                            onClick={() => handleAction(SubjectActionTypes.SUBJECT_READ, item)}
                            className="font-bold text-base text-color-000 hover:text-primary cursor-pointer leading-snug">
                            {item?.title}
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                            <SubjectIdCode code={item?.subject_idcode} t={t} pill/>
                            <SubjectHashtags hashtags={item?.hashtags}/>
                        </div>
                    </div>
                </div>
                <div className="flex-shrink-0 contextIcon">
                    {renderContextMenu(item)}
                </div>
            </div>
            {renderIf(
                item?.description,
                <div className="pt-2.5 text-xs text-color-300 tracking-wider max-lines-2 whitespace-pre-line">
                    {item?.description}
                </div>
            )}
            <div className="pt-2 border-t border-color-50 flex items-center justify-between gap-3">
                <SubjectUsagePills item={item} t={t}/>
                <button
                    type="button"
                    onClick={() => handleAction(SubjectActionTypes.SUBJECT_READ, item)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-50 hover:bg-primary-100 text-primary-600 font-semibold text-xs whitespace-no-wrap">
                    {t('view detail')}
                    <BsChevronRight size={11}/>
                </button>
            </div>
        </div>
    )
}
